<script setup lang="ts">
import { computed, onMounted, ref, type Component } from 'vue'
import { useRoute } from 'vue-router'

import ArtifactIcon from '@/components/ArtifactIcon.vue'
import RocketIcon from '@/components/RocketIcon.vue'
import SpaceSwitcher from '@/components/SpaceSwitcher.vue'
import { apexUrl } from '@/lib/host'
import { useSession } from '@/stores/session'

const { state, currentSpace, canManage, canViewBilling, bootstrap, signIn, signOut } =
  useSession()

const route = useRoute()

const sidebarOpen = ref(false)

onMounted(bootstrap)

/**
 * Nav items: a Heroicons outline path plus a one-line description.
 *
 * The paths are inline rather than an icon component so they inherit
 * `stroke="currentColor"` and pick up the active/hover colour from the link.
 * `route.path` is compared directly instead of relying on router active classes
 * so the active styling cannot lose a specificity race with the base colour.
 *
 * `requires` names the capability an item needs; an omitted value means every
 * member sees it.
 *
 * An item carries either a `d` outline path drawn on a 24×24 grid, or its own
 * `iconComponent` when the mark does not fit that grid (the artifacts cube is a
 * filled 640×640 path). The two are mutually exclusive and the template renders
 * one or the other, so no item ends up with two marks.
 */
interface NavItem {
  to: string
  label: string
  description: string
  d?: string
  iconComponent?: Component
  requires?: 'manage' | 'billing'
}

const allNavItems: NavItem[] = [
  {
    to: '/members',
    label: 'Members',
    description: 'Who can call this space',
    // Heroicons v2 outline: user-group
    d: 'M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z',
  },
  {
    to: '/credits',
    label: 'Credit Usage',
    description: 'Calls and balance',
    // Heroicons v2 outline: queue-list
    d: 'M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z',
  },
  {
    to: '/artifacts',
    label: 'Artifacts',
    description: 'Pages published',
    iconComponent: ArtifactIcon,
  },
  {
    to: '/analytics',
    label: 'Analytics',
    description: 'Tokens and cost over time',
    // Heroicons v2 outline: chart-bar
    d: 'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z',
  },
  {
    to: '/billing',
    label: 'Billing',
    description: 'Top-ups and receipts',
    // Heroicons v2 outline: credit-card
    d: 'M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z',
    requires: 'billing',
  },
  {
    to: '/settings',
    label: 'Settings',
    description: 'Space name',
    // Heroicons v2 outline: cog-6-tooth (gear outline + hub, one path)
    d: 'M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.991l1.005.827c.424.354.534.955.26 1.431l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.28z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
    requires: 'manage',
  },
]

/**
 * Billing and Settings are staff screens. Billing additionally stops at admin —
 * a manager reads usage, not the funding history — and Settings needs the
 * manage capability. Items are removed rather than disabled, so nobody is shown
 * a door they cannot open.
 */
const navItems = computed(() =>
  allNavItems.filter((item) => {
    switch (item.requires) {
      case 'billing':
        return canViewBilling.value
      case 'manage':
        return canManage.value
      default:
        return true
    }
  }),
)

/**
 * Every space page needs the same three preconditions, so they are resolved once
 * here rather than repeated in each page: we must know who you are, we must have
 * found the space named by this hostname, and you must be a member of it.
 */
const blocked = computed(() => {
  if (state.status === 'idle' || state.status === 'loading') return 'loading'
  if (state.status === 'anonymous') return 'anonymous'
  if (state.status === 'error') return 'error'
  if (!currentSpace.value) return 'no-access'
  return null
})
</script>

<template>
  <div class="flex min-h-screen">
    <!-- Backdrop, small screens only -->
    <div
      v-if="sidebarOpen"
      class="fixed inset-0 z-30 bg-black/60 lg:hidden"
      @click="sidebarOpen = false"
    />

    <aside
      class="fixed inset-y-0 left-0 z-40 flex w-[304px] flex-col border-r border-base-content/10 bg-base-200 transition-transform lg:sticky lg:top-0 lg:h-screen lg:shrink-0 lg:translate-x-0"
      :class="sidebarOpen ? 'translate-x-0' : '-translate-x-full'"
    >
      <!-- Wordmark -->
      <a
        :href="apexUrl('/')"
        class="flex items-center gap-3 border-b border-base-content/10 px-6 py-5 transition-colors hover:bg-base-content/5"
      >
        <RocketIcon class="size-6 text-primary" />
        <span class="text-lg font-semibold tracking-tight">OYO AI Space</span>
      </a>

      <div class="border-b border-base-content/10 px-3 py-4">
        <SpaceSwitcher @navigated="sidebarOpen = false" />
      </div>

      <nav class="flex-1 overflow-y-auto p-3">
        <div class="flex flex-col gap-1">
          <RouterLink
            v-for="item in navItems"
            :key="item.to"
            :to="item.to"
            class="flex items-start gap-3 rounded-box px-3 py-2.5 transition-colors"
            :class="
              route.path === item.to
                ? 'bg-primary/15 text-primary'
                : 'text-base-content/70 hover:bg-base-content/5 hover:text-base-content'
            "
            :aria-current="route.path === item.to ? 'page' : undefined"
            @click="sidebarOpen = false"
          >
            <component
              :is="item.iconComponent"
              v-if="item.iconComponent"
              class="mt-0.5 size-5 shrink-0"
            />
            <svg
              v-else
              class="mt-0.5 h-5 w-5 shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.6"
              aria-hidden="true"
            >
              <path stroke-linecap="round" stroke-linejoin="round" :d="item.d" />
            </svg>
            <span class="min-w-0">
              <span class="block text-sm font-medium">{{ item.label }}</span>
              <span class="block truncate text-xs text-base-content/45">
                {{ item.description }}
              </span>
            </span>
          </RouterLink>
        </div>
      </nav>

      <div class="border-t border-base-content/10 px-6 py-5">
        <p class="truncate text-sm text-base-content/60">
          Signed as
          <span class="font-medium text-base-content">{{ state.account?.alias || '—' }}</span>
        </p>
        <button class="btn btn-neutral mt-4 w-full" @click="signOut">Sign Out</button>
      </div>
    </aside>

    <div class="flex min-w-0 flex-1 flex-col">
      <!-- Small-screen header -->
      <header
        class="sticky top-0 z-20 flex items-center gap-3 border-b border-base-content/10 bg-base-300/90 px-4 py-3 backdrop-blur lg:hidden"
      >
        <button
          class="btn btn-square btn-ghost"
          aria-label="Open navigation"
          @click="sidebarOpen = true"
        >
          <i class="fa-solid fa-bars" aria-hidden="true" />
        </button>
        <RocketIcon class="size-5 text-primary" />
        <span class="text-base font-semibold">OYO AI Space</span>
      </header>

      <main class="min-w-0 flex-1 px-5 py-6 lg:px-8 lg:py-8">
        <!-- Loading -->
        <div v-if="blocked === 'loading'" class="mx-auto max-w-6xl space-y-4">
          <div class="skeleton h-8 w-56" />
          <div class="skeleton h-40 w-full" />
        </div>

        <!-- Anonymous -->
        <div
          v-else-if="blocked === 'anonymous'"
          class="mx-auto flex max-w-md flex-col items-center gap-4 py-24 text-center"
        >
          <i class="fa-solid fa-lock text-2xl text-base-content/30" aria-hidden="true" />
          <h1 class="text-xl font-semibold">Sign in to continue</h1>
          <p class="text-sm text-base-content/60">
            This dashboard belongs to a space. Sign in with the account that was invited.
          </p>
          <button class="btn btn-primary" @click="signIn">Sign in with Google</button>
        </div>

        <!-- Session error -->
        <div
          v-else-if="blocked === 'error'"
          class="mx-auto max-w-md py-24 text-center"
        >
          <i class="fa-solid fa-triangle-exclamation text-2xl text-error" aria-hidden="true" />
          <h1 class="mt-4 text-xl font-semibold">We could not load this space</h1>
          <p class="mt-2 text-sm text-base-content/60">{{ state.error }}</p>
          <RouterLink to="/members" class="btn mt-4">Retry</RouterLink>
        </div>

        <!-- No membership -->
        <div v-else-if="blocked === 'no-access'" class="mx-auto max-w-lg py-24">
          <div class="rounded-box border border-base-content/10 bg-base-100 p-8 text-center">
            <i class="fa-solid fa-user-slash text-2xl text-base-content/30" aria-hidden="true" />
            <h1 class="mt-4 text-xl font-semibold">No access to this space</h1>
            <p class="mt-2 text-sm text-base-content/60">
              <span class="tnum">{{ $route.path }}</span> — this space does not exist, or
              <strong class="font-medium text-base-content/80">{{ state.account?.email }}</strong>
              is not a member of it.
            </p>
            <div class="mt-6 flex justify-center gap-2">
              <a :href="apexUrl('/create-space')" class="btn btn-primary">
                <i class="fa-solid fa-plus" aria-hidden="true" />
                Create a space
              </a>
              <button class="btn btn-ghost" @click="signOut">Switch account</button>
            </div>
          </div>
        </div>

        <slot v-else />
      </main>
    </div>
  </div>
</template>
